import { ApolloClient, gql } from '@apollo/client'
import { countLeaves, countSkippedLeaves } from '@/services/ParentalControlsService/preference-tree'
import { GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { Maybe } from '@/utils/types'

export const GET_SKIP_CATEGORY_PREFERENCES_FOR_USER = gql`
  query getSkipCategoryPreferences($language: String!, $id: String) {
    user {
      id
      parentalControlPreferences(language: $language, contentId: $id) {
        id
        key
        displayTitle
        parentId
        isNew
        totalCount
        skipped
        skippedCount
      }
    }
  }
`

export const UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER = gql`
  mutation updateSkipCategoryPreferences($language: String!, $updates: [ParentalControlPreferenceUpdateInput!]!) {
    updateParentalControlPreferences(language: $language, updates: $updates) {
      id
      key
      displayTitle
      parentId
      skipped
      skippedCount
      totalCount
    }
  }
`

export const SET_TV_PARENTAL_CONTROLS_SAVE_AND_WATCH_AT = gql`
  mutation setTvParentalControlsSaveAndWatchAt {
    setTvParentalControlsSaveAndWatchAt
  }
`

export type SerializedSkipCategoryPreference = NonNullable<
  NonNullable<NonNullable<GetSkipCategoryPreferencesQuery['user']>['parentalControlPreferences']>[number]
>

export interface SkipCategoryPreferenceSummary {
  parentalControlsApplied: number
  parentalControlsTotal: number
  hasParentalControls: boolean
  preferences: SerializedSkipCategoryPreference[]
}

interface GetSkipCategoryPreferencesForProjectParams {
  client: ApolloClient
  contentId: Maybe<string>
  lang: string
}

export const getSkipCategoryPreferencesForProject = async ({
  client,
  contentId,
  lang,
}: GetSkipCategoryPreferencesForProjectParams): Promise<SkipCategoryPreferenceSummary> => {
  const empty: SkipCategoryPreferenceSummary = {
    parentalControlsApplied: 0,
    parentalControlsTotal: 0,
    hasParentalControls: false,
    preferences: [],
  }
  try {
    if (!contentId) return empty

    const result = await client.query<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
      query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
      variables: { language: lang, id: contentId },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })

    const preferences = (result?.data?.user?.parentalControlPreferences ?? []).filter(isDefined)
    const parentalControlsTotal = countLeaves(preferences)
    const parentalControlsApplied = countSkippedLeaves(preferences)

    return {
      parentalControlsApplied,
      parentalControlsTotal,
      hasParentalControls: parentalControlsTotal > 0,
      preferences,
    }
  } catch (error) {
    logger().error('getSkipCategoryPreferencesForProject failed', { contentId, lang }, error)
    return empty
  }
}
